import ReactDOM from 'react-dom/client'
import 'antd/dist/antd.min.css'
import './index.css'
import App from './App'
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router-dom'
import { Provider } from 'react-redux'
import { store } from './app/store/Store'
import './i18n'
import 'swiper/css'
import { MainGuard } from './app/guards/MainGuard'
import { AuthGuard } from './app/guards/AuthGuard'
import { ProjectsPage } from './pages/projects/ProjectsPage'
import { PageGuard } from './app/guards/PageGuard'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { TeamPage } from './pages/team/TeamPage'
import { OnboardingPage } from './pages/onboarding/OnboardingPage'
import { NotFound } from './shared/components/cards/not-found/NotFound'
import { LiveSearchPage } from './pages/live-search/LiveSearchPage'
import { LanguageCompliancePage } from './pages/language-compliance/LanguageCompliancePage'
import { PPCCompliancePage } from './pages/ppc-compliance/PPCCompliancePage'
import { ProfileSettingsPage } from './pages/profile-settings/ProfileSettingsPage'
import { OrganicResultsPage } from './pages/organic-results/OrganicResultsPage'
import { DomainOverviewPage } from './pages/domain-overview/DomainOverviewPage'
import { PositionTrackerPage } from './pages/position-tracker/PositionTrackerPage'
import { SERPPresencePage } from './pages/serp-presence/SERPPresencePage'
import { BlackhatMonitoringPage } from './pages/blackhat-monitoring/BlackhatMonitoringPage'
import { LiveUncloakingPage } from './pages/live-uncloaking/LiveUncloakingPage'
import { PaidResultsPage } from './pages/paid-results/PaidResultsPage'
import { AdOverviewPage } from './pages/ad-overview/AdOverviewPage'
import { AffiliatesAdsPage } from './pages/affiliates-ads/AffiliatesAdsPage'
import { AdvertisingResearchPage } from './pages/advertising-research/AdvertisingResearchPage'
import { AdsPage } from './pages/ads/AdsPage'
import { AdvertisersPage } from './pages/advertisers/AdvertisersPage'
import { AuthLayout } from './app/layouts/auth-layout/AuthLayout'
import { AuthPage } from './pages/auth/AuthPage'
import { SignupPage } from './pages/signup/SignupPage'
import { ResetPasswordPage } from './pages/reset-password/ResetPasswordPage'
import { SetUpPasswordPage } from './pages/set-up-password/SetUpPasswordPage'
import { ProjectPage } from './pages/project/ProjectPage'
import { ErrorPage } from './widgets/utility/ErrorPage'
import { AppErrorPage } from './widgets/utility/AppErrorPage'
import { VerifyEmailPage } from './pages/verify-email/VerifyEmailPage'
import { LinkEmailPage } from './pages/link-email/LinkEmailPage'
import { ReferralProgramPage } from './pages/referral-program/ReferralProgramPage'
import { ReferralCodePage } from './pages/referral-code/ReferralCodePage'
import { AuditRequest } from './pages/audit-request/AuditRequest'
import { MainLayout } from './app/layouts/main-layout/MainLayout'
import { TrademarkInfringementsPage } from './pages/trademark-infringements/TrademarkInfringementsPage'
import { DemoRequest } from './pages/demo-request/DemoRequest'

const isRobuscan = process.env.REACT_APP_PROJECT === 'robuscan'

const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <AppErrorPage />,
    children: [
      {
        path: 'auth',
        element: <AuthGuard />,
        children: [
          {
            element: <AuthLayout />,
            children: [
              {
                element: <Outlet />,
                errorElement: <ErrorPage />,
                children: [
                  { index: true, element: <AuthPage /> },
                  {
                    path: 'signup',
                    element: isRobuscan ? <Navigate to={'/demo-request'} /> : <SignupPage />,
                  },
                  {
                    path: 'reset-password',
                    element: <ResetPasswordPage />,
                  },
                  {
                    path: 'set-up-password',
                    element: <SetUpPasswordPage />,
                  },
                  {
                    path: 'request-demo',
                    element: <Navigate to={isRobuscan ? '/demo-request' : '/auth/signup'} />,
                  },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '/',
        element: <MainGuard />,
        children: [
          {
            element: <MainLayout />,
            children: [
              {
                element: <Outlet />,
                errorElement: <ErrorPage />,
                children: [
                  { index: true, element: <Navigate to={'/projects'} /> },
                  {
                    path: 'projects',
                    element: (
                      <PageGuard roles={['editor', 'admin']}>
                        <ProjectsPage />
                      </PageGuard>
                    ),
                  },
                  {
                    path: 'projects/:id/:tab?',
                    element: (
                      <PageGuard roles={['editor', 'admin']}>
                        <ProjectPage />
                      </PageGuard>
                    ),
                  },
                  {
                    path: 'team',
                    element: (
                      <PageGuard roles={['admin']}>
                        <TeamPage />
                      </PageGuard>
                    ),
                  },
                  { path: 'ad-reports', element: <PaidResultsPage /> },
                  {
                    path: 'adv-overview',
                    children: [
                      { index: true, element: <AdOverviewPage /> },
                      { path: ':publisher_id/:affiliate', element: <AffiliatesAdsPage /> },
                    ],
                  },
                  {
                    path: 'advertising-research',
                    children: [
                      { index: true, element: <AdvertisingResearchPage /> },
                      { path: 'ads-domain/:id/:domain', element: <AdsPage /> },
                      { path: 'ads-keyword/:id/:keyword', element: <AdsPage /> },
                      { path: 'advertisers/:id/:keyword', element: <AdvertisersPage /> },
                    ],
                  },
                  { path: 'profile-settings', element: <ProfileSettingsPage /> },
                  { path: 'referral-program', element: <ReferralProgramPage /> },
                  { path: 'organic-results', element: <OrganicResultsPage /> },
                  { path: 'detailed-info', element: <DomainOverviewPage /> },
                  { path: 'position-tracker', element: <PositionTrackerPage /> },
                  { path: 'serp-presence', element: <SERPPresencePage /> },
                  { path: 'blackhat-monitoring', element: <BlackhatMonitoringPage /> },
                  { path: 'live-uncloaking', element: <LiveUncloakingPage /> },
                  { path: 'ppc-compliance', element: <PPCCompliancePage /> },
                  { path: 'language-compliance-page', element: <LanguageCompliancePage /> },
                  { path: 'tm-infringement', element: <TrademarkInfringementsPage /> },
                  { path: 'live-search', element: <LiveSearchPage /> },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'onboarding',
        element: <MainGuard />,
        children: [{ index: true, element: <OnboardingPage /> }],
      },
      {
        path: 'r/:id',
        element: <AuthGuard />,
        children: [{ index: true, element: <ReferralCodePage /> }],
      },
      {
        path: 'audit-request',
        element: <AuthGuard />,
        children: [
          {
            index: true,
            element: <AuditRequest />,
          },
        ],
      },
      {
        path: 'demo-request',
        element: <AuthGuard />,
        children: [
          {
            index: true,
            element: <DemoRequest />,
          },
        ],
      },
      {
        path: 'verify-email',
        element: <VerifyEmailPage />,
      },
      { path: 'link-email', element: <LinkEmailPage /> },
    ],
  },
])

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <Provider store={store}>
    <GoogleOAuthProvider
      clientId={
        process.env.REACT_APP_BUILD_ENV?.includes('prod')
          ? '1098248718474-3fgvrgu8rhpl7lo1ob0vmaj2tkoqs1t6.apps.googleusercontent.com'
          : '1098248718474-pn7cnfqhnsij259125mph9po549noikf.apps.googleusercontent.com'
      }
    >
      <RouterProvider router={router} />
    </GoogleOAuthProvider>
  </Provider>,
)
